import { useEffect, useState } from 'react';
import axios from 'axios';
import { showToast } from "../../Components/utils/ShowToast";
import { FaEdit, FaTrash } from 'react-icons/fa';
import Loader from '../../Components/utils/Loader';
import ConfirmDialog from '../../Components/utils/ReuseConfirmDialog';
import ReactQuill from 'react-quill';
import ImageCropModal from '../../Components/utils/ImageCropModel';
import 'react-quill/dist/quill.snow.css';

function AdminBlog() {
    const [blogs, setBlogs] = useState([]);
    const [title, setTitle] = useState('');
    const [category, setCategory] = useState('Recipes');
    const [slug, setSlug] = useState('');
    const [content, setContent] = useState('');
    const [image, setImage] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState({ open: false, id: null });
    const [cropModalOpen, setCropModalOpen] = useState(false);
    const [imageToCrop, setImageToCrop] = useState(null);

    const token = localStorage.getItem('adminToken');

    useEffect(() => {
        fetchAllBlogs();
    }, []);

    const fetchAllBlogs = async () => {
        try {
            setIsLoading(true);
            const { data } = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/blogs`);
            if (data.success) {
                setBlogs(data.blogs);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddOrUpdate = async (e) => {
        e.preventDefault();
        if (!title.trim() || !category || !slug.trim() || !content.trim()) {
            showToast('All fields are required', 'warning');
            return;
        }
        setIsLoading(true);
        try {
            const formData = new FormData();
            formData.append('title', title);
            formData.append('category', category);
            formData.append('slug', slug);
            formData.append('content', content);
            if (image) {
                formData.append('image', image);
            }

            if (editMode) {
                const { data } = await axios.put(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/blogs/${editId}`, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });
                if (data.success) {
                    showToast('Blog updated successfully', 'success');
                    resetForm();
                    fetchAllBlogs();
                }
            } else {
                const { data } = await axios.post(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/blogs`, formData, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                });
                if (data.success) {
                    showToast('Blog created successfully', 'success');
                    resetForm();
                    fetchAllBlogs();
                }
            }
        } catch (error) {
            showToast(error?.response?.data?.error || 'Something went wrong!', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleEdit = (blog) => {
        setEditMode(true);
        setEditId(blog._id);
        setTitle(blog.title);
        setCategory(blog.category);
        setSlug(blog.slug);
        setContent(blog.content);
    };

    const handleDeleteClick = (id) => {
        setShowDeleteConfirm({ open: true, id });
    };

    const handleConfirmDelete = async () => {
        setIsLoading(true);
        try {
            const id = showDeleteConfirm.id;
            const { data } = await axios.delete(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/blogs/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (data.success) {
                showToast('Blog deleted successfully', 'success');
                fetchAllBlogs();
            }
        } catch (error) {
            showToast(error?.response?.data?.error || 'Something went wrong!', 'error');
        } finally {
            setIsLoading(false);
            setShowDeleteConfirm({ open: false, id: null });
        }
    };

    const handleCancelDelete = () => {
        setShowDeleteConfirm({ open: false, id: null });
    };

    const resetForm = () => {
        setTitle('');
        setCategory('Recipes');
        setSlug('');
        setContent('');
        setImage(null);
        setEditMode(false);
        setEditId(null);
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                const img = new Image();
                img.onload = () => {
                    const w = img.width;
                    const h = img.height;
                    const ratio = w / h;
                    const targetRatio = 550 / 330;
                    if (Math.abs(ratio - targetRatio) < 0.01) {
                        setImage(file);
                    } else {
                        setImageToCrop({ dataUrl: reader.result, filename: file.name });
                        setCropModalOpen(true);
                    }
                };
                img.src = reader.result;
            };
            reader.readAsDataURL(file);
        }
    };

    const handleCropComplete = (croppedFile) => {
        setImage(croppedFile);
        setCropModalOpen(false);
    };

    return (
        <>
            {isLoading && <Loader type="checkmate" />}
            <div className='adminBlogDiv'>
                <div className='adminBlogContent'>
                    <form onSubmit={handleAddOrUpdate} className='form'>
                        <h2 className='secondaryHeading'>Blog Management</h2>
                        <div className="horizontalLine"></div>

                        <div className="inputDiv">
                            <label>Title <span>*</span></label>
                            <input
                                type='text'
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder='Enter blog title'
                                className='inputField'
                                required
                                disabled={isLoading}
                            />
                        </div>

                        <div className="inputDiv">
                            <div className="inputInnerDiv">
                                <label>Category <span>*</span></label>
                                <select
                                    value={category}
                                    onChange={(e) => setCategory(e.target.value)}
                                    className='inputField dropdownLight'
                                    required
                                    disabled={isLoading}
                                >
                                    <option value="Recipes">Recipes</option>
                                    <option value="Healthy">Healthy</option>
                                    <option value="Yummy">Yummy</option>
                                </select>
                            </div>

                            <div className="inputInnerDiv">
                                <label>Slug <span>*</span></label>
                                <input
                                    type='text'
                                    value={slug}
                                    onChange={(e) => setSlug(e.target.value)}
                                    placeholder='Enter unique slug'
                                    className='inputField'
                                    required
                                    disabled={isLoading}
                                />
                            </div>
                        </div>

                        <div className="inputDiv">
                            <label>Main Image</label>
                            <input
                                type='file'
                                accept='image/*'
                                onChange={handleImageChange}
                                className='inputField'
                                disabled={isLoading}
                            />
                        </div>

                        <div className="inputDiv">
                            <label>Content <span>*</span></label>
                            <ReactQuill
                                value={content}
                                onChange={setContent}
                                theme="snow"
                                className='reactQuill'
                            />
                        </div>

                        <div className="buttonsRow">
                            <button type='submit' className='primaryBtn' disabled={isLoading}>
                                {editMode ? 'Update Blog' : 'Add Blog'}
                            </button>
                            {editMode && (
                                <button
                                    type='button'
                                    className='secondaryBtn'
                                    onClick={resetForm}
                                    disabled={isLoading}
                                >
                                    Cancel Edit
                                </button>
                            )}
                        </div>
                    </form>

                    <div className="horizontalLine"></div>

                    <h3 className="adminBlogListHeading">Blog List</h3>
                    <div className='adminBlogGrid'>
                        {blogs.length === 0 && (
                            <p className="adminBlogEmpty">No blogs have been added yet.</p>
                        )}
                        {blogs.map((blog) => (
                            <div key={blog._id} className='adminBlogCard'>
                                <div className='adminBlogCardImage'>
                                    <img src={blog.image || '/placeholder.svg'} alt={blog.title} />
                                    <span className="adminBlogCardCategory">{blog.category}</span>
                                </div>
                                <div className='adminBlogCardContent'>
                                    <h3 className="adminBlogCardTitle">{blog.title}</h3>
                                    <p className="adminBlogCardDate">{new Date(blog.date).toLocaleDateString()}</p>
                                    <div className='adminBlogCardActions'>
                                        <button
                                            onClick={() => handleEdit(blog)}
                                            className="editBtn"
                                            disabled={isLoading}
                                            title="Edit"
                                        >
                                            <FaEdit />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteClick(blog._id)}
                                            className="deleteBtn"
                                            disabled={isLoading}
                                            title="Delete"
                                        >
                                            <FaTrash />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {cropModalOpen && (
                    <ImageCropModal
                        imageSrc={imageToCrop?.dataUrl}
                        open={cropModalOpen}
                        onCancel={() => setCropModalOpen(false)}
                        onComplete={handleCropComplete}
                        aspect={550 / 330}
                        filename={imageToCrop?.filename}
                    />
                )}

                <ConfirmDialog
                    open={showDeleteConfirm.open}
                    title="Confirm Deletion"
                    message="Are you sure you want to delete this blog?"
                    onConfirm={handleConfirmDelete}
                    onCancel={handleCancelDelete}
                    isLoading={isLoading}
                />
            </div>
        </>
    );
}

export default AdminBlog;